import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../utils/api';
import { PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';

const ImageUpload = ({ currentImage, onImageChange, menuItemName = 'menu item' }) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);

    setUploading(true);
    try {
      const response = await api.post('/upload/menu-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        onImageChange(response.data.data.image_url);
        toast.success('Image uploaded successfully');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Clear the input
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!currentImage) return;

    if (!confirm(`Are you sure you want to delete the image for ${menuItemName}?`)) {
      return;
    }

    setDeleting(true);
    try {
      await api.delete('/upload/menu-image', {
        data: { image_url: currentImage }
      });
      
      onImageChange(null);
      toast.success('Image deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete image');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold text-gray-700">Image</label>
      
      {currentImage ? (
        <div className="relative group">
          <img 
            key={`${currentImage}-${uploading}`}
            src={`http://localhost:8000${currentImage}?nocache=${Math.random()}`} 
            alt={menuItemName}
            className="w-full h-32 object-cover rounded border border-gray-300"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
            }}
          />
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <TrashIcon className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="border border-dashed border-gray-300 rounded p-3 text-center bg-gray-50">
          <PhotoIcon className="mx-auto h-8 w-8 text-gray-400" />
          <p className="text-xs text-gray-600 mt-0.5">No image</p>
        </div>
      )}

      <label className="block">
        <input
          type="file"
          accept="image/*"
          onChange={handleUpload}
          disabled={uploading}
          className="sr-only"
        />
        <div className={`
          cursor-pointer inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded w-full
          ${uploading 
            ? 'bg-gray-300 text-gray-600 cursor-not-allowed' 
            : 'bg-blue-600 text-white hover:bg-blue-700'
          } transition-colors
        `}>
          <PhotoIcon className="w-3.5 h-3.5" />
          {uploading ? 'Uploading...' : currentImage ? 'Change' : 'Upload'}
        </div>
      </label>

      <p className="text-xs text-gray-500 text-center">Max 5MB</p>
    </div>
  );
};

export default ImageUpload;
